/**
 * useOrientation — returns a stable `isLandscape` boolean that only changes
 * when the device orientation actually flips, not on every intermediate
 * dimension float during the system rotation animation.
 *
 * MEASURED CORRECTION (2026-08-15): this hook's original rationale was wrong,
 * recorded here so nobody optimises against it again.
 *
 *   The claim was that iOS "delivers a continuous stream of dimension updates"
 *   during rotation, rebuilding MapView on every frame. Profiling MapView on an
 *   iPhone 16 Pro simulator shows a rotation produces 3 renders at ~5ms each,
 *   not a per-frame flood — React Native's own `useWindowDimensions` already
 *   skips updates when width/height/scale/fontScale are unchanged.
 *
 *   The real cost is the PRICE of a MapView render, not the count: opening
 *   world-classic (42 territories) costs ~1.9s, a rotation ~300ms.
 *   See docs/map-perf.md -> "MEASURED".
 *
 * So this hook is a small, harmless win (a consumer needing only the boolean
 * re-renders on the flip rather than ~3x) and NOT a fix for rotation jank. Do
 * not build machinery on the assumption that it is.
 *
 * The fix:
 *   Derive `isLandscape = width > height` and publish it via a separate
 *   state slot. Because `setState` in React bails out without a re-render
 *   when the value hasn't changed (Object.is semantics on primitives), the
 *   consumer only re-renders when the boolean actually flips — i.e., when
 *   the device completes a landscape↔portrait transition, not on every
 *   intermediate frame.
 *
 * Usage:
 *   const { isLandscape } = useOrientation()
 *
 *   Replace `const { width, height } = useWindowDimensions()` +
 *   `const isLandscape = width > height` in any screen that only needs the
 *   orientation boolean and not the raw dimensions.
 *
 *   If you also need `width` or `height` for numeric layout (e.g. a pinned
 *   sidebar width), keep using `useWindowDimensions` for those values and
 *   use this hook only for the orientation branch.
 */

import React from 'react'
import { useWindowDimensions } from 'react-native'

export interface OrientationState {
  isLandscape: boolean
}

/**
 * Pure orientation decision — exported for unit tests.
 *
 * Keyboard-stable: a soft keyboard (notably on mobile web, where it resizes the
 * layout viewport) only ever SHRINKS the window HEIGHT; a real device rotation
 * swaps BOTH dimensions. Given the current dims, the width at the last accepted
 * decision (`lastWidth`), and the current `isLandscape`, returns the next value.
 *
 *  - `height >= width` → unambiguously portrait (a keyboard can't fake this, it
 *    only shrinks height). Always `false`. This is what CORRECTS a screen that
 *    evaluated while a keyboard was open and got stuck landscape.
 *  - `width > height` is ambiguous (real landscape, OR portrait with keyboard
 *    open). Only `true` when the WIDTH changed (a real rotation); otherwise keep
 *    the current value so a keyboard-only height shrink can't flip the layout.
 */
export function resolveOrientation(
  width: number,
  height: number,
  lastWidth: number,
  current: boolean,
): boolean {
  if (height >= width) return false
  if (width !== lastWidth) return true
  return current
}

export function useOrientation(): OrientationState {
  const { width, height } = useWindowDimensions()

  // Derive the boolean and store it separately. React's `setState` compares
  // with Object.is — two identical `false` or `true` values short-circuit
  // without scheduling a re-render, so callers are only notified on a flip.
  const [isLandscape, setIsLandscape] = React.useState<boolean>(() => width > height)

  const lastWidth = React.useRef(width)
  React.useEffect(() => {
    const prevWidth = lastWidth.current
    lastWidth.current = width
    setIsLandscape((current) => resolveOrientation(width, height, prevWidth, current))
  }, [width, height])

  return React.useMemo(() => ({ isLandscape }), [isLandscape])
}
